/**
 * @file WorkspaceDesktop.tsx
 * @description Рабочая область конструктора от 1280 (uiux-builder-shell.md
 * §2, §9): навигатор · канва · панель настроек; разделы без живого
 * предпросмотра (SEO, настройки сайта, история) раскрываются на место канвы,
 * сайт — кнопкой «Показать сайт», правой колонки тогда нет.
 * Колонки тянутся ручками и сворачиваются в полоску (партия после приёмки Р2а):
 * кнопка «Свернуть» навигатора — в его правом верхнем углу (у канвы), панели —
 * в её шапке рядом с [×].
 */

import { Maximize2 } from "lucide-react";

import { Button } from "../../ui/button";
import { useBuilder } from "../builder-context";
import { BuilderNav } from "../nav/BuilderNav";
import { BuilderPanel } from "../panels/BuilderPanel";
import { CanvasColumn } from "./CanvasColumn";
import { CollapseButton, CollapsedStrip, ColumnHandle, type ColumnsApi } from "./column-controls";
import { cn } from "@/lib/utils";

interface Props {
  api: ColumnsApi;
  /** Раздел раскрыт на место канвы — правой колонки нет. */
  spread: boolean;
  /** Высота области (классы). */
  heightClass: string;
}

export function WorkspaceDesktop({ api, spread, heightClass }: Props) {
  const b = useBuilder();
  const { widths } = api;
  const nav = widths.columns.nav;
  const panel = widths.columns.panel;

  return (
    <div ref={widths.containerRef} style={widths.style} data-testid="builder-workspace" className={cn("grid min-h-0", heightClass)}>
      {nav?.collapsed ? (
        <CollapsedStrip
          side="left"
          label="Показать структуру сайта"
          caption="Структура сайта"
          minWidth={nav.min}
          autoFocus={api.focusKey === "nav"}
          onExpand={(w) => widths.expand("nav", w)}
        />
      ) : (
        <>
          <aside aria-label="Структура сайта" className="relative min-h-0 overflow-y-auto overscroll-contain p-4">
            <CollapseButton side="left" label="Свернуть структуру сайта" onClick={() => api.collapse("nav")} className="absolute right-2 top-2 z-10" />
            <BuilderNav />
          </aside>
          <ColumnHandle api={api} column="nav" />
        </>
      )}
      {spread ? (
        <div className="flex min-h-0 min-w-0 flex-col gap-2 p-4">
          <Button variant="outline" className="self-end" onClick={b.openWholeSite}>
            <Maximize2 aria-hidden />
            Показать сайт
          </Button>
          <BuilderPanel spread className="flex-1" />
        </div>
      ) : (
        <>
          <CanvasColumn />
          {panel?.collapsed ? (
            <CollapsedStrip
              side="right"
              label="Показать панель настроек"
              caption="Настройки"
              minWidth={panel.min}
              autoFocus={api.focusKey === "panel"}
              onExpand={(w) => widths.expand("panel", w)}
            />
          ) : (
            <>
              <ColumnHandle api={api} column="panel" />
              {/* Чат в конструкторе — значком в верхней панели (SC64), угол свободен. */}
              <BuilderPanel
                className="min-h-0 p-4"
                headerExtra={<CollapseButton side="right" label="Свернуть панель настроек" onClick={() => api.collapse("panel")} />}
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
